import clsx from "clsx";
import { motion, useScroll, useTransform } from "motion/react";
import { useEffect, useRef, useState } from "react";

import imageJohn from "../assets/Team/John.jpg";
import imageKarolis from "../assets/Team/Karolis.jpg";
import imageMarkku from "../assets/Team/Markku.jpg";
import imageMatt from "../assets/Team/Matt.jpg";
import imagePaulius from "../assets/Team/Paulius.jpg";
import imageRasmus from "../assets/Team/Rasmus.jpg";
import imageSvyat from "../assets/Team/Svyat.jpg";
import boltLogo from "../assets/TeamLogos/Bolt.png";
import freshaLogo from "../assets/TeamLogos/Fresha.png";
import googleLogo from "../assets/TeamLogos/Google.png";
import revolutLogo from "../assets/TeamLogos/Revolut.png";
import unityLogo from "../assets/TeamLogos/Unity.png";
import wixLogo from "../assets/TeamLogos/Wix.png";

import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useIsMobile } from "./ui/use-mobile";

interface TeamMember {
  name: string;
  title: string;
  image: string;
}

const teamMembers: TeamMember[] = [
  {
    name: "Matt",
    title: "Co-founder / CEO",
    image: imageMatt,
  },
  {
    name: "Svyat",
    title: "Co-founder / Design",
    image: imageSvyat,
  },
  {
    name: "Paulius",
    title: "CTO",
    image: imagePaulius,
  },
  {
    name: "Karolis",
    title: "Founding Staff Engineer",
    image: imageKarolis,
  },
  {
    name: "Rasmus",
    title: "Founding Staff Engineer",
    image: imageRasmus,
  },
  {
    name: "Markku",
    title: "AI Engineer",
    image: imageMarkku,
  },
  {
    name: "John",
    title: "Architect",
    image: imageJohn,
  },
];

const CARD_WIDTH = 320;

export default function TeamCarousel() {
  const isMobile = useIsMobile();

  return isMobile ? <TeamCarouselMobile /> : <TeamCarouselDesktop />;
}

function TeamCarouselMobile() {
  // Mobile: Continuous carousel
  return (
    <section className="relative w-full py-16">
      <PreviouslyAt isMobile={true} />

      <div className="relative overflow-hidden">
        <motion.div
          className="flex gap-4"
          animate={{
            x: [0, -CARD_WIDTH * teamMembers.length - 16 * teamMembers.length],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 30,
              ease: "linear",
            },
          }}
        >
          {/* Render team members twice for seamless loop */}
          {[...teamMembers, ...teamMembers].map((member, index) => (
            <div
              key={`${member.name}-${index}`}
              className="flex-shrink-0"
              style={{ width: 280 }}
            >
              <div className="relative">
                <div className="relative mb-3 aspect-[3/4] overflow-hidden rounded-2xl bg-gray-100">
                  <ImageWithFallback
                    src={member.image}
                    alt={member.name}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="space-y-1">
                  <h3
                    className="text-white"
                    style={{ fontSize: "var(--text-lg)", fontWeight: "600" }}
                  >
                    {member.name}
                  </h3>
                  <p
                    className="text-gray-400"
                    style={{ fontSize: "var(--text-sm)" }}
                  >
                    {member.title}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function TeamCarouselDesktop() {
  const targetRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [scrollDimension, setScrollDimension] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const calculateDimensions = () => {
      if (!containerRef.current) return;

      const scrollWidth = containerRef.current.scrollWidth;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // The horizontal distance we need to travel
      const distance = Math.max(0, scrollWidth - viewportWidth);

      // Total height of the section = viewport height (for sticky) + scroll distance
      // This ensures 1px of vertical scroll = 1px of horizontal scroll
      const height = distance + viewportHeight;

      setScrollDimension({ width: distance, height });
    };

    calculateDimensions();
    window.addEventListener("resize", calculateDimensions);

    // Also recalculate after a short delay to ensure images/layout have settled
    const timeout = setTimeout(calculateDimensions, 100);

    return () => {
      window.removeEventListener("resize", calculateDimensions);
      clearTimeout(timeout);
    };
  }, []);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], [0, -scrollDimension.width]);

  // Use a reasonable default height for server-side rendering or initial paint
  // to minimize layout shift, though the exact height will snap in after hydration/effect
  const sectionHeight = scrollDimension.height || "300vh";

  // Desktop: Scroll-based carousel
  return (
    <section
      ref={targetRef}
      style={{ height: sectionHeight }}
      className="relative w-full"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="w-full">
          <PreviouslyAt isMobile={false} />

          <motion.div
            ref={containerRef}
            style={{ x }}
            className="flex w-max gap-6 pr-4 pl-4 md:pr-8 md:pl-8 lg:pr-16 lg:pl-16"
          >
            {teamMembers.map((member, index) => (
              <div
                key={`${member.name}-${index}`}
                className="flex-shrink-0"
                style={{ width: CARD_WIDTH }}
              >
                <div className="group relative">
                  <div className="relative mb-4 aspect-[3/4] overflow-hidden rounded-2xl bg-gray-100">
                    <ImageWithFallback
                      src={member.image}
                      alt={member.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </div>

                  <div className="space-y-1">
                    <h3
                      className="text-white"
                      style={{ fontSize: "var(--text-xl)", fontWeight: "600" }}
                    >
                      {member.name}
                    </h3>
                    <p
                      className="text-gray-400"
                      style={{ fontSize: "var(--text-metadata)" }}
                    >
                      {member.title}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const previousCompanies = [
  { name: "Google", logo: googleLogo, marginTop: 4 },
  { name: "Revolut", logo: revolutLogo, marginTop: 1 },
  { name: "Bolt", logo: boltLogo, marginTop: 2 },
  { name: "Unity", logo: unityLogo, marginTop: 0 },
  { name: "Wix", logo: wixLogo, marginTop: -1 },
  { name: "Fresha", logo: freshaLogo, marginTop: 2 },
];

type PreviouslyAtProps = {
  isMobile: boolean;
};

function PreviouslyAt({ isMobile }: PreviouslyAtProps) {
  return (
    <div
      className={
        "mx-auto w-full max-w-[1600px] px-4 " +
        (isMobile ? "mb-6 space-y-3" : "mb-12 space-y-6 md:px-8 lg:px-16")
      }
    >
      <h2
        className="leading-[1.1] tracking-tight text-white"
        style={{ fontSize: "var(--text-section)", fontWeight: "600" }}
      >
        Built by a team from Europe's best
      </h2>

      <div className="flex h-auto flex-wrap items-center gap-6">
        <span
          className="flex-shrink-0 text-gray-400"
          style={{ fontSize: "var(--text-metadata)" }}
        >
          Previously at
        </span>
        {previousCompanies.map(({ name, logo, marginTop }) => (
          <div
            key={name}
            className={clsx(
              "flex flex-shrink-0 items-center",
              isMobile ? "h-4" : "h-5",
            )}
            style={{ marginTop: marginTop }}
          >
            {/* Logo PNGs are dark-on-transparent; flip them white for the
                dark page. */}
            <ImageWithFallback
              src={logo}
              alt={name}
              className="h-full w-auto object-contain brightness-0 invert"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
